import React, { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import GlobalLoader from "../../common/GlobalLoader";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import EmptyPage from "../../common/emptyPage";
import OverallMaterialPopup from "../../common/OverallMaterialPopup";
import ViewMaterialPopup from "../../common/ViewMaterialPopup";
import { AddIcon, FileIcon } from "../../../icons";
import pdfIcon from "../../../assets/svg/pdfIcon.svg";
import playIcon from "../../../assets/png/playIcon.png";
import audioIcon from "../../../assets/png/audioIcon.png";
import imgIcon from "../../../assets/png/imgIcon.png";
import powerpointIcon from "../../../assets/png/powerpointIcon.png";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  getOverallMaterials,
  createOverallMaterial,
  updateOverallMaterial,
  deleteOverallMaterial,
  getOverallMaterial,
} from "../../../helper/apicalls/overallMaterials";
import "../../../styles/admin/overall.scss";

const OverallMaterialsPage = () => {
  const { setHeaderName } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingMaterial, setEditingMaterial] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [viewMaterial, setViewMaterial] = useState(null);

  const renderMaterialIcon = (item) => {
    const type = (item?.materials_type || "").toLowerCase();

    if (type === "pdf") {
      return (
        <img
          src={pdfIcon}
          alt="PDF Icon"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }

    if (type === "video" || type === "videos") {
      return (
        <img
          src={playIcon}
          alt="Video Icon"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }

    if (type === "audio" || type === "audios") {
      return (
        <img
          src={audioIcon}
          alt="Audio Icon"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }

    if (type === "img" || type === "image" || type === "images") {
      return (
        <img
          src={imgIcon}
          alt="Image Icon"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }

    if (type === "powerpoint" || type === "ppt" || type === "pptx") {
      return (
        <img
          src={powerpointIcon}
          alt="PowerPoint Icon"
          style={{ width: "48px", height: "48px", marginTop: "4px" }}
        />
      );
    }

    return <FileIcon style={{ color: "var(--text-secondary)" }} />;
  };

  const resetForm = () => {
    setEditingMaterial(null);
    setIsPopupOpen(false);
  };

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      setError(null);
      const courseKey = (searchParams.get("course") || "").trim();
      const data = await getOverallMaterials(courseKey);
      setMaterials(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Unable to load guides and materials. Please try again.");
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMaterial = async (item) => {
    const id = item?.id || item?._id;
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const material = await getOverallMaterial(id);

      const mappedMaterial = {
        ...material,
        materials_type: material?.materials_type || material?.type || "",
        content: material?.content || material?.link || material?.url || "",
        name: material?.name || material?.title || "",
      };

      setViewMaterial(mappedMaterial);
    } catch (err) {
      setError("Unable to load guide/material. Please try again.");
    } finally {
      setLoading(false);
      closeMenu();
    }
  };

  useEffect(() => {
    setHeaderName("Guides & Materials");
    fetchMaterials();
  }, [setHeaderName, searchParams]);

  useEffect(() => {
    if (openMenuIndex === null) return;

    const handleClickOutside = (event) => {
      const menus = document.querySelectorAll(
        ".overall-tests-menu, .overall-tests-menu-button",
      );
      for (let i = 0; i < menus.length; i += 1) {
        const el = menus[i];
        if (el && el.contains(event.target)) {
          return;
        }
      }

      setOpenMenuIndex(null);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuIndex]);

  const handlePopupSave = async (popupPayload, file) => {
    try {
      setLoading(true);
      setError(null);

      const basePayload = {
        content: popupPayload?.content || "",
        materials_type: popupPayload?.materials_type || "pdf",
        name: popupPayload?.name || "",
      };

      const courseKey = (searchParams.get("course") || "").trim();

      if (editingMaterial?.id || editingMaterial?._id) {
        const id = editingMaterial.id || editingMaterial._id;
        await updateOverallMaterial(id, basePayload, file);
      } else {
        await createOverallMaterial(basePayload, courseKey, file);
      }

      resetForm();
      await fetchMaterials();
    } catch (err) {
      setError("Failed to save guide/material. Please try again.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (item) => {
    const id = item?.id || item?._id;
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const material = await getOverallMaterial(id);

      const mappedMaterial = {
        ...material,
        id: material?.id || id,
        materials_type: material?.materials_type || material?.type || "pdf",
        name: material?.name || material?.title || "",
        content: material?.content || material?.link || material?.url || "",
      };

      setEditingMaterial(mappedMaterial);
      setIsPopupOpen(true);
    } catch (err) {
      setError("Unable to load guide/material for editing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setSelectedForDelete(id || null);
    setIsConfirmDeleteOpen(true);
  };

  const toggleMenu = (e, index) => {
    e.stopPropagation();
    setOpenMenuIndex((prev) => (prev === index ? null : index));
  };

  const closeMenu = () => setOpenMenuIndex(null);

  const handleConfirmDelete = async () => {
    if (!selectedForDelete) {
      setIsConfirmDeleteOpen(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await deleteOverallMaterial(selectedForDelete);
      setIsConfirmDeleteOpen(false);
      setSelectedForDelete(null);
      fetchMaterials();
    } catch (err) {
      setError("Failed to delete guide/material. Please try again.");
      setIsConfirmDeleteOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overall-tests-container">
      <GlobalLoader visible={loading} text="Loading guides & materials..." />
      <div className="overall-tests-card">
        <div className="overall-tests-header">
          <span className="overall-tests-title">Guides & Materials</span>
          <div className="overall-tests-actions">
            <button
              type="button"
              className="overall-tests-add-btn"
              onClick={() => {
                setEditingMaterial(null);
                setIsPopupOpen(true);
              }}
              disabled={loading}
            >
              <AddIcon />
              <span>{editingMaterial ? "Edit Material" : "Add Material"}</span>
            </button>
          </div>
        </div>

        <div className="overall-tests-grid">
          {!loading &&
            !error &&
            materials?.map((item, index) => (
              <div
                key={item?.id || item?._id || item?.title}
                className="overall-tests-item"
                onClick={() => {
                  handleViewMaterial(item);
                }}
              >
                <div className="overall-tests-menu">
                  <button
                    type="button"
                    className="overall-tests-menu-button"
                    onClick={(e) => toggleMenu(e, index)}
                  >
                    <MoreVertIcon className="overall-tests-menu-icon" />
                  </button>
                  {openMenuIndex === index && (
                    <div
                      className="overall-tests-menu-list"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="overall-tests-menu-item"
                        onClick={() => {
                          handleEdit(item);
                          closeMenu();
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="overall-tests-menu-item overall-tests-menu-item--danger"
                        onClick={() => {
                          handleDelete(item?.id || item?._id);
                          closeMenu();
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                <div className="overall-tests-icon">
                  {renderMaterialIcon(item)}
                </div>
                <div className="overall-tests-name">
                  {item?.name || item?.title || "Untitled Material"}
                </div>
              </div>
            ))}
        </div>

        {error && !loading && <EmptyPage content={error} />}

        {!loading && !error && materials?.length === 0 && (
          <EmptyPage content={"No guides or materials found."} />
        )}

        <OverallMaterialPopup
          open={isPopupOpen}
          handleClose={resetForm}
          onSave={handlePopupSave}
          mode={editingMaterial ? "edit" : "create"}
          material={
            editingMaterial
              ? {
                  ...editingMaterial,
                  name: editingMaterial.name || editingMaterial.title,
                  content: editingMaterial.content || "",
                }
              : null
          }
        />

        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          handleDelete={handleConfirmDelete}
          title="Delete Guide / Material"
          message="Are you sure you want to delete this guide / material? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />

        {viewMaterial && (
          <ViewMaterialPopup
            open={!!viewMaterial}
            handleClose={() => setViewMaterial(null)}
            material={viewMaterial}
          />
        )}
      </div>
    </div>
  );
};

export default OverallMaterialsPage;
